// web/src/lib/dayTimeline.js
// One person's day, gathered into a single time-ordered column.
//
// Until now a trainer's day was told in three places that never met: the
// bookable slots came from /slots, the time off came from the day endpoint, and
// the shift they were rostered on wasn't on the availability calendar at all.
// So a Tuesday could look wide open at eight in the morning and be nothing of
// the kind, because eight to ten was morning feed.
//
// This is the join. Everything that occupies an hour becomes an entry of the
// same shape, and the page draws them against one axis — which is what a
// Day-Timer page is, and why a barn recognises it on sight.
//
// Pure. No React, no fetch, no Date built from a local timezone, so the
// arithmetic that decides what sits where can be tested without a browser and
// does not change behaviour on the day the clocks do.

import { dayAxis, barGeometry, assignLanes, toMinutes } from './shifts.js';

/**
 * Where an entry sits on a VERTICAL axis, as percentages.
 *
 * The same arithmetic as the roster's horizontal bars, turned on its side —
 * deliberately the same function rather than a second copy of it, because two
 * implementations of "where does 9:15 fall" drift, and the way you find out is
 * a calendar that disagrees with the Gantt one tap away.
 */
export function stackGeometry(item, axis) {
  const { left, width } = barGeometry(item, axis);
  return { top: left, height: width };
}

/**
 * Everything occupying an hour of this person's day, as one list.
 *
 * `kind` is what the entry IS, and it decides both how it draws and whether it
 * can be touched:
 *
 *   shift   work they are rostered on. Read-only here — a shift is changed on
 *           the roster, where whoever owns the roster can see the whole week.
 *   off     time off with hours on it. Grey, and says why.
 *   booked  a lesson somebody has booked. Read-only: a client turning up is
 *           not something to undo by tapping their name on a calendar.
 *   open    an unbooked teaching slot. The one kind that can still be changed.
 *
 * An all-day absence is NOT returned here. It has no hours to draw against, and
 * a block filling the column would bury anything that survived it. The caller
 * washes the whole day instead.
 */
export function dayEntries({ shifts = [], timeOff = [], slots = [] } = {}) {
  const out = [];

  for (const s of shifts) {
    out.push({
      id: `shift-${s.id}`,
      kind: 'shift',
      startTime: s.startTime,
      endTime: s.endTime,
      title: s.typeName || 'At the barn',
      detail: s.notes || null,
      color: s.color || 'slate',
      source: s,
    });
  }

  for (const t of timeOff) {
    if (t.allDay || !t.startTime || !t.endTime) continue;
    out.push({
      id: `off-${t.id}`,
      kind: 'off',
      startTime: t.startTime,
      endTime: t.endTime,
      title: t.reason || 'Time off',
      detail: null,
      color: null,
      source: t,
    });
  }

  for (const s of slots) {
    const booked = (s.riders ?? []).length > 0;
    out.push({
      // A slot has no id of its own — it is generated from an availability row
      // and a start time, and both are needed to tell two of them apart.
      id: `slot-${s.availabilityId}-${s.startTime}`,
      kind: booked ? 'booked' : 'open',
      startTime: s.startTime,
      endTime: s.endTime,
      // An open slot says so in words. It used to be told only by a dashed
      // border, which meant a morning of three unbooked hours read as three
      // dashed boxes and nothing on the page said "open" — and a border is a
      // poor place to keep a fact somebody has to act on.
      title: booked
        ? (s.riders[0].lessonTypeName || s.lessonTypeName || 'Lesson')
        : (s.lessonTypeName ? `${s.lessonTypeName} · Open` : 'Open'),
      detail: null,
      color: null,
      source: s,
    });
  }

  return out.sort(
    (a, b) =>
      (toMinutes(a.startTime) ?? 0) - (toMinutes(b.startTime) ?? 0) ||
      (toMinutes(a.endTime) ?? 0) - (toMinutes(b.endTime) ?? 0) ||
      a.id.localeCompare(b.id),
  );
}

/**
 * Which column an entry belongs in.
 *
 * A shift is CONTEXT — the reason the afternoon is spoken for — and it runs
 * beside the day rather than in it. Everything else is the day itself.
 *
 * One shared set of lanes was the obvious thing and it was wrong. Laning
 * everything together made a shift claim the first lane for the seven hours it
 * ran, so the lessons inside it were pushed right and the lessons after it
 * dropped back left. Nothing had moved in the barn; the page just re-flowed
 * under the reader, and a column that means "morning" at ten and "afternoon" at
 * three is worse than no column at all.
 */
function groupOf(kind) {
  return kind === 'shift' ? 'context' : 'day';
}

/**
 * The whole drawing: the entries, the axis they hang on, and how wide each of
 * the two columns has to be.
 *
 * Lanes are still how an overlap shows as an overlap — two lessons booked over
 * each other is a real mistake and drawing them on top of one another hides
 * exactly the fact worth seeing. They are just counted within a column now, so
 * a shift running through the morning doesn't shove the whole day sideways.
 */
export function dayPlan(input = {}) {
  const entries = dayEntries(input);
  const axis = dayAxis(entries);

  const byGroup = { context: [], day: [] };
  for (const e of entries) byGroup[groupOf(e.kind)].push(e);

  const laned = {
    context: assignLanes(byGroup.context),
    day: assignLanes(byGroup.day),
  };

  return {
    entries: entries.map((e) => {
      const group = groupOf(e.kind);
      return {
        ...e,
        group,
        lane: laned[group].laneOf.get(e.id) ?? 0,
        ...stackGeometry(e, axis),
      };
    }),
    axis,
    // Zero when there is nothing in that column, so the caller can give the
    // whole width to the day rather than reserving a gutter for nothing.
    contextLanes: byGroup.context.length ? laned.context.lanes : 0,
    dayLanes: byGroup.day.length ? laned.day.lanes : 0,
  };
}
